<template>
  <div
    class="hero-tag-chip"
    :class="{
      'has-art': hasArt,
      [`rarity-${normalizedRarity}`]: hasArt,
      block,
    }"
    :data-rarity="hasArt ? normalizedRarity : undefined"
  >
    <div class="chip-copy">
      <span class="chip-name">{{ name }}</span>
      <span v-if="meta" class="chip-meta">{{ meta }}</span>
      <slot />
    </div>
    <button
      v-if="removable"
      type="button"
      class="chip-remove"
      @click.stop="$emit('remove')"
    >
      ×
    </button>
    <div v-if="hasArt" class="chip-art">
      <img
        class="chip-art-img"
        :src="image"
        :style="cardImageStyle(imagePosition)"
        alt=""
      />
      <div class="rarity-gradient-overlay"></div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { cardImageStyle } from "../utils/cardImagePosition";

const props = defineProps({
  name: { type: String, required: true },
  meta: { type: String, default: "" },
  image: { type: String, default: "" },
  imagePosition: { type: Object, default: null },
  rarity: { type: String, default: "common" },
  removable: { type: Boolean, default: false },
  block: { type: Boolean, default: false },
});

defineEmits(["remove"]);

const hasArt = computed(() => Boolean(props.image));
const normalizedRarity = computed(() => props.rarity || "common");
</script>

<style scoped>
.hero-tag-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 240, 255, 0.12);
  border: 1px solid rgba(0, 240, 255, 0.4);
  color: #00f0ff;
  font-size: 0.8rem;
  --rarity-color: #718096;
}

.hero-tag-chip.block {
  display: flex;
  width: 100%;
  box-sizing: border-box;
}

.hero-tag-chip.has-art {
  min-width: 200px;
  min-height: 2.6rem;
  background: var(--rarity-color);
  border: 1px solid var(--rarity-color);
  color: #e7ecf1;
  box-shadow: 0 0 10px color-mix(in srgb, var(--rarity-color) 35%, transparent);
}

.hero-tag-chip.rarity-common {
  --rarity-color: #718096;
}
.hero-tag-chip.rarity-uncommon {
  --rarity-color: #38a169;
}
.hero-tag-chip.rarity-rare {
  --rarity-color: #3182ce;
}
.hero-tag-chip.rarity-epic {
  --rarity-color: #805ad5;
}
.hero-tag-chip.rarity-legendary {
  --rarity-color: #d69e2e;
}

.chip-copy {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  min-width: 0;
}

.hero-tag-chip:not(.block) .chip-copy {
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
}

.chip-name {
  font-weight: 600;
}

.chip-meta {
  opacity: 0.9;
}

.chip-remove {
  position: relative;
  z-index: 2;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0 0.15rem;
}

.chip-remove:hover {
  color: #bf616a;
}

.chip-art {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 70%;
  overflow: hidden;
  z-index: 0;
}

.chip-art-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rarity-gradient-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    to right,
    var(--rarity-color) 30%,
    transparent 100%
  );
}
</style>
